/**
 * Country-driven date/time formatting for the browser.
 *
 * The JavaScript half of the "wire vs human" rule from issue GH-2008
 * ("Project-wide date & time formatting"): wdDate/wdDateTime/wdTime/
 * wdLongDate are the only place a date becomes a string for a person to
 * read. Everything else - API payloads, sort keys, filenames - stays
 * ISO-8601 and must not go through this module.
 *
 * Formats are seeded once from window.WD_DATE_FORMATS, a bootstrap emitted
 * by templates/base.html from wastedesk.formats.countries.COUNTRY_FORMATS
 * (via wastedesk/processors.py's get_wd_date_formats()), so the tokens here
 * always match whatever wastedesk.formats.fmt_date and the `wd_date` /
 * `wd_datetime` / `wd_time` / `wd_long_date` template filters produce
 * server-side for the same CURRENT_COUNTRY.
 *
 * Deliberately does not call moment.locale(...): that mutates a global and
 * races between components (issue GH-2008, Phase 3). Locale is passed
 * per-call to moment() / .locale() instead, exactly like
 * mixins.js:formatDateTimeInt already does.
 */
import moment from 'moment';

// Conservative fallback for contexts where base.html hasn't run yet (e.g. a
// unit test importing this module directly) - NL, matching
// settings.CURRENT_COUNTRY's own default.
const FALLBACK_FORMATS = {
    date: 'DD-MM-YYYY',
    datetime: 'DD-MM-YYYY HH:mm',
    time: 'HH:mm',
    longDate: 'D MMMM YYYY',
    months: [
        'januari',
        'februari',
        'maart',
        'april',
        'mei',
        'juni',
        'juli',
        'augustus',
        'september',
        'oktober',
        'november',
        'december',
    ],
    daySuffix: '',
};

export function activeFormats() {
    return (typeof window !== 'undefined' && window.WD_DATE_FORMATS) || FALLBACK_FORMATS;
}

// Mirrors mixins.js:formatDateTimeInt - moment ships an 'nl' locale file,
// not a separate 'nl-be' one.
export function momentLocale(langCode) {
    if (!langCode) {
        return 'en';
    }
    return langCode === 'nl-be' ? 'nl' : langCode;
}

export function activeLocale() {
    return momentLocale(typeof window !== 'undefined' ? window.user_lang : undefined);
}

/**
 * Render a date/datetime/ISO string as the active country's short numeric
 * date (e.g. "01-01-2027" for NL, "01/01/2027" for BE/FR/LU).
 */
export function wdDate(value) {
    if (!value) {
        return '';
    }
    return moment(value).format(activeFormats().date);
}

/**
 * Render a date/datetime/ISO string as the active country's date + time.
 */
export function wdDateTime(value) {
    if (!value) {
        return '';
    }
    return moment(value).format(activeFormats().datetime);
}

/**
 * Render a time/datetime/ISO string as the active country's time (HH:mm
 * everywhere).
 */
export function wdTime(value) {
    if (!value) {
        return '';
    }
    return moment(value).format(activeFormats().time);
}

/**
 * Render a date/datetime/ISO string as the active country's long form
 * (PDFs, invoices), e.g. "1 januari 2027" for NL, "1. Januar 2027" for DE.
 *
 * Deliberately does NOT format the month name through moment's "MMMM"
 * token: that resolves via moment's *locale*, which tracks the active UI
 * *language* (window.user_lang), not CURRENT_COUNTRY - exactly the bug
 * this project exists to fix (a Czech-speaking user on a Dutch install
 * would get Dutch digits with Czech month names). Instead this builds the
 * string from window.WD_DATE_FORMATS.months, the same country-keyed table
 * wastedesk.formats.fmt_long_date uses server-side - see
 * wastedesk/formats/countries.py and issue GH-2008.
 *
 * { includeWeekday: true } prepends a weekday name (e.g. "maandag 1
 * januari 2027") for the handful of callers that previously built this via
 * a hardcoded Intl.DateTimeFormat - see mixins.js:formatDateInt. Unlike
 * the month name, the weekday word still follows the active UI language
 * (there's no country-keyed weekday table - fmt_long_date has no weekday
 * variant either), which is acceptable here because every current
 * includeWeekday caller is a live editing widget, not a legal document.
 */
export function wdLongDate(value, { includeWeekday = false } = {}) {
    if (!value) {
        return '';
    }
    const m = moment(value);
    if (!m.isValid()) {
        return 'Invalid date';
    }
    const formats = activeFormats();
    const longDate = `${m.date()}${formats.daySuffix} ${formats.months[m.month()]} ${m.year()}`;
    if (!includeWeekday) {
        return longDate;
    }
    const weekday = moment(value).locale(activeLocale()).format('dddd');
    return `${weekday} ${longDate}`;
}

/**
 * moment.js format-TOKEN string (not a formatted value) for the active
 * country's long date form, for interactive widgets that render dates
 * through their own third-party :lang/locale system (vue-datepicker-next,
 * FullCalendar) rather than through wdLongDate above. Escapes the country's
 * day-suffix as literal text via moment's `[...]` syntax (e.g. "D[.]" for
 * DE/CZ's "1." vs NL/BE/FR/LU/PL's bare "1") so it isn't parsed as a format
 * token. Month/weekday names still resolve via the widget's own locale pack
 * - see datepicker.vue for why those stay language-driven rather than
 * switching to this module's country-keyed `months` table.
 */
export function pickerLongDateFormat({ includeWeekday = true } = {}) {
    const suffix = activeFormats().daySuffix;
    const dayToken = suffix ? `D[${suffix}]` : 'D';
    return includeWeekday ? `dddd ${dayToken} MMMM YYYY` : `${dayToken} MMMM YYYY`;
}

/**
 * pickerLongDateFormat plus a time token, for pickers bound to a
 * DateTimeField. A picker left on the date-only format cannot display - let
 * alone edit - the time half of the value it holds. Every country in
 * wastedesk/formats/countries.py uses the same 24-hour clock (_TIME = "H:i"),
 * so this needs no per-country branch.
 */
export function pickerLongDateTimeFormat({ includeWeekday = true } = {}) {
    return `${pickerLongDateFormat({ includeWeekday })} HH:mm`;
}

export default { wdDate, wdDateTime, wdTime, wdLongDate };
